import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Wallet, Banknote, Loader2, Clock, HandCoins, Check, X, CheckCircle2 } from "lucide-react";

const PRESETS = [0, 10, 15, 20];

export function PaymentPanel({ errand, isRequester, isRunner, onChange }) {
  const [busy, setBusy] = useState("");
  const [pct, setPct] = useState(0);
  const [custom, setCustom] = useState("");
  const base = Number(errand.budget);
  const tip = custom !== "" ? Math.max(0, Number(custom) || 0) : +(base * (pct / 100)).toFixed(2);
  const total = +(base + tip).toFixed(2);
  const settledTip = Number(errand.tip || 0);
  const settledTotal = +(base + settledTip).toFixed(2);

  const payCard = async () => {
    setBusy("card");
    try {
      const { data } = await api.post("/payments/checkout", { errand_id: errand.id, origin_url: window.location.origin, method: "card", tip });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(""); }
  };
  const payCash = async () => {
    setBusy("cash");
    try {
      const { data } = await api.post(`/errands/${errand.id}/pay-cash`, { tip });
      toast.success("Cash request sent — your runner will confirm receipt.");
      onChange(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const confirmCash = async () => {
    setBusy("confirm");
    try {
      const { data } = await api.post(`/errands/${errand.id}/confirm-cash`);
      toast.success("Cash confirmed — payment settled!");
      onChange(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const cancelCash = async () => {
    setBusy("cancelcash");
    try {
      const { data } = await api.post(`/errands/${errand.id}/cancel-cash`);
      onChange(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  // Cash awaiting runner confirmation
  if (errand.cash_pending) {
    if (isRunner) {
      return (
        <div data-testid="runner-confirm-cash" className="space-y-2">
          <div className="flex items-start gap-2 bg-[#FFF7ED] border border-[#FCE3C4] rounded-2xl p-3 text-sm text-[#9A6212]">
            <HandCoins size={16} className="mt-0.5 shrink-0" />
            <span>{errand.requester_name} chose to pay <strong>${settledTotal.toFixed(2)}</strong> in cash{settledTip > 0 ? ` (incl. $${settledTip.toFixed(2)} tip)` : ""}. Confirm once you've received it.</span>
          </div>
          <Button data-testid="confirm-cash-btn" onClick={confirmCash} disabled={busy === "confirm"}
            className="w-full rounded-full h-12 font-bold bg-[#2F8F6E] hover:bg-[#267a5d] text-white active:scale-95 transition-transform">
            {busy === "confirm" ? <Loader2 className="animate-spin" /> : <><Check size={18} className="mr-2" /> Confirm cash received</>}
          </Button>
        </div>
      );
    }
    return (
      <div data-testid="requester-cash-pending" className="space-y-2">
        <div className="flex items-start gap-2 bg-[#EEF6FF] border border-[#D6E7FA] rounded-2xl p-3 text-sm text-[#2C6FA0]">
          <Clock size={15} className="mt-0.5 shrink-0" />
          <span>Waiting for {errand.runner_name} to confirm they received <strong>${settledTotal.toFixed(2)}</strong> in cash{settledTip > 0 ? ` (incl. $${settledTip.toFixed(2)} tip)` : ""}.</span>
        </div>
        <Button data-testid="cancel-cash-btn" onClick={cancelCash} disabled={busy === "cancelcash"} variant="ghost"
          className="w-full rounded-full h-10 font-semibold text-[#6B7280] hover:bg-gray-100">
          <X size={15} className="mr-1.5" /> Switch payment method
        </Button>
      </div>
    );
  }

  // Runner waiting for requester to pay
  if (isRunner) {
    return (
      <div data-testid="runner-awaiting-payment" className="flex items-start gap-2 bg-[#FFF7ED] border border-[#FCE3C4] rounded-2xl p-3 text-sm text-[#9A6212]">
        <Clock size={15} className="mt-0.5 shrink-0" />
        <span>Errand complete! Waiting for {errand.requester_name} to release your <strong>${base.toFixed(0)}</strong> payment.</span>
      </div>
    );
  }

  // Requester payment step (tip + method)
  return (
    <div data-testid="payment-panel" className="rounded-2xl border-2 border-primary/30 bg-primary/[0.03] p-4 space-y-3">
      <div>
        <p className="font-bold text-sm text-[#1A1D20] mb-2 flex items-center gap-1.5"><HandCoins size={15} className="text-primary" /> Add a tip for {errand.runner_name}?</p>
        <div className="grid grid-cols-4 gap-2">
          {PRESETS.map((p) => (
            <button key={p} type="button" data-testid={`tip-${p}`}
              onClick={() => { setPct(p); setCustom(""); }}
              className={`rounded-xl border-2 py-2 text-sm font-bold transition-colors ${custom === "" && pct === p ? "border-primary bg-primary/10 text-primary" : "border-gray-200 text-[#4A5361]"}`}>
              {p === 0 ? "No tip" : `${p}%`}
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-[#6B7280]">Custom $</span>
          <Input data-testid="tip-custom" type="number" min="0" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="0.00"
            className="rounded-xl h-10 border-gray-200 flex-1" />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-2">
        <span className="text-[#6B7280]">Errand ${base.toFixed(2)}{tip > 0 ? ` + tip $${tip.toFixed(2)}` : ""}</span>
        <span data-testid="pay-total" className="font-heading font-extrabold text-lg text-[#1A1D20]">${total.toFixed(2)}</span>
      </div>

      <Button data-testid="pay-errand-btn" onClick={payCard} disabled={!!busy}
        className="w-full rounded-full h-12 font-bold active:scale-95 transition-transform">
        {busy === "card" ? <Loader2 className="animate-spin" /> : <><Wallet size={18} className="mr-2" /> Pay with card · ${total.toFixed(2)}</>}
      </Button>
      <Button data-testid="pay-cash-btn" onClick={payCash} disabled={!!busy} variant="outline"
        className="w-full rounded-full h-12 font-bold border-2">
        {busy === "cash" ? <Loader2 className="animate-spin" /> : <><Banknote size={18} className="mr-2" /> Pay in cash · ${total.toFixed(2)}</>}
      </Button>
      <p className="text-[11px] text-[#9CA3AF] text-center leading-tight">
        Card is secured by Stripe (Apple/Google Pay supported). Cash is settled in person — your runner confirms receipt.
      </p>
    </div>
  );
}

// Shown to the requester AFTER a card payment — lets them add an extra tip.
export function AddTipCard({ errand }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(15);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const base = Number(errand.budget);
  const tip = custom !== "" ? Math.max(0, Number(custom) || 0) : +(base * (pct / 100)).toFixed(2);
  const already = Number(errand.tip || 0);

  const sendTip = async () => {
    if (tip <= 0) { toast.error("Enter a tip greater than $0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/errands/${errand.id}/add-tip`, { tip, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div data-testid="requester-paid-note" className="rounded-2xl border border-[#CDEBDF] bg-[#E7F6F0] p-4">
      <div className="flex items-center gap-2 text-sm font-bold text-[#2F8F6E]">
        <CheckCircle2 size={16} /> Paid ${(base + already).toFixed(2)}{errand.payment_method === "cash" ? " (cash)" : ""}{already > 0 ? ` · $${already.toFixed(2)} tip` : ""}
      </div>
      {!open ? (
        <Button data-testid="add-tip-open-btn" onClick={() => setOpen(true)} variant="ghost"
          className="mt-2 h-9 rounded-full font-bold text-primary hover:bg-primary/10">
          <HandCoins size={15} className="mr-1.5" /> {already > 0 ? "Add another tip" : "Tip your runner"}
        </Button>
      ) : (
        <div className="mt-3 space-y-3" data-testid="add-tip-panel">
          <div className="grid grid-cols-3 gap-2">
            {[10, 15, 20].map((p) => (
              <button key={p} type="button" data-testid={`addtip-${p}`} onClick={() => { setPct(p); setCustom(""); }}
                className={`rounded-xl border-2 py-2 text-sm font-bold transition-colors ${custom === "" && pct === p ? "border-primary bg-primary/10 text-primary" : "border-gray-200 text-[#4A5361] bg-white"}`}>
                {p}%
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#6B7280]">Custom $</span>
            <Input data-testid="addtip-custom" type="number" min="0" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="0.00"
              className="rounded-xl h-10 border-gray-200 flex-1 bg-white" />
          </div>
          <Button data-testid="send-tip-btn" onClick={sendTip} disabled={busy}
            className="w-full rounded-full h-11 font-bold active:scale-95 transition-transform">
            {busy ? <Loader2 className="animate-spin" /> : <><HandCoins size={16} className="mr-2" /> Send ${tip.toFixed(2)} tip</>}
          </Button>
        </div>
      )}
    </div>
  );
}
